<template>
	<div
		class="relative w-full flex flex-col md:flex-row justify-start items-center overflow-hidden"
		:style="{
			height: `${pageHeight}px`,
		}"
	>
		<div
			ref="contentContainer"
			class="relative px-10 py-10 max-w-6xl h-fit"
			:style="{
				width: `${contentWidth}px`,
			}"
		>
			<h1 class="text-xl md:text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-display mb-4 md:mb-8">
				{{ $t('aboutPage.welcome') }}
			</h1>

			<p class="text-[17px] leading-[27px] mb-4 md:mb-8">
				{{ $t('aboutPage.aboutText') }}
			</p>

			<div class="flex justify-center items-start flex-col mt-4 gap-0.5">
				<span class="text-sm">
					{{ $t('discover.suggesting') }}:
				</span>
				<div class="flex justify-start items-center gap-2 flex-wrap">
					<a
						v-for="link, i in discoverLinks"
						:key="i"
						:href="link.link" 
						target="_blank"
						class="text-sm md:text-md lg:text-lg xl:text-xl text-left text-primary-2 hover:text-primary-1 pr-2 border-secondary-2 capitalize"
						:class="i === discoverLinks.length - 1 ? 'border-0' : 'border-r'"
					>
						<span>{{ link.name }}</span>
					</a>
				</div>
			</div>

			<!-- <div class="relative w-3/4 h-fit hidden md:block">
				<p class="text-lg lg:text-xl xl:text-2xl font-medium">
					See our apartments and the utilities included
				</p>
			</div> -->

			<!-- <div class="relative w-full h-fit mt-8 block md:hidden">
				<p class="text-sm font-semibold">
					See our apartments and the utilities included by clicking on the apartment color below
				</p>
			</div> -->
		</div>

		<ApartmentAboutBar
			v-for="apartment, i in apartments"
			:key="i"
			:ref="`apartment-${i}`"
			:i="i"
			:apartment="apartment"
			:barWidth="barWidth"
			:barHeight="barHeight"
			:barTop="barTop"
			:barLeft="barLeft"
			:contentWidth="contentWidth"
			:contentHeight="contentHeight"
			@select="selectApartment(i)"
		/>
	</div>
</template>

<script>
import gsap from 'gsap';
import { discoverLinks, apartments } from '@/assets/config/infoConfig';
import ApartmentAboutBar from './graphics/ApartmentAboutBar.vue';

export default {
	name: 'MultiApartmentAboutPage',
	setup() {
		return {
			discoverLinks,
			apartments,
		};
	},
	components: {
		ApartmentAboutBar,
	},
	data() {
		return {
			pageHeight: window.innerHeight * 0.9,

			barWidth: 0,
			barHeight: 0,
			barTop: 0,
			barLeft: 0,
			contentWidth: 0,
			contentHeight: 0,

			selectedApartment: -1,

			galleryOpen: false,

			wasResized: 0,
			lastWidth: 0,
		};
	},
	beforeMount() {
		this.$nextTick(() => {
			this.calculateContentSize();
		});
		setTimeout(() => {
			this.calculateContentSize();
		}, 100);
		window.addEventListener('resize', this.calculateContentSize);
	},
	mounted() {
		this.animateFirstBar();
	},
	beforeUnmount() {
		window.removeEventListener('resize', this.calculateContentSize);
	},
	methods: {
		animateFirstBar() {
			const duration = 0.5;
			const delay = 0.5;
			const move = -50;
			
			const vars = window.innerWidth < 768 ? {
				x: 0,
				y: move,
				duration: duration,
				repeat: -1,
				yoyo: true,
				repeatDelay: delay,
			} : {
				x: move,
				y: 0,
				duration: duration,
				repeat: -1,
				yoyo: true,
				repeatDelay: delay,
			};

			gsap.fromTo(this.$refs[`apartment-0`][0].$el, {
				x: 0,
				y: 0,
				duration: duration,
			}, vars);
		},
		calculateContentSize() {
			if (this.lastWidth === 0) {
				this.lastWidth = window.innerWidth;
			} else if (this.lastWidth !== window.innerWidth) {
				this.wasResized = true;
				this.lastWidth = window.innerWidth;
			}

			if (window.innerWidth < 768) {
				this.pageHeight = this.$refs.contentContainer.getBoundingClientRect().height + this.apartments.length * 40;

				if (this.wasResized) {
					this.selectApartment(-1);
					this.wasResized = false;
				}

				this.contentWidth = window.innerWidth;
				this.barWidth = window.innerWidth;
				this.barHeight = 40;
				this.contentHeight = this.pageHeight - this.apartments.length * this.barHeight;
				this.barLeft = 0;
				this.barTop = this.contentHeight;
			} else {
				if (this.wasResized) {
					this.selectApartment(-1);
					this.wasResized = false;
				}

				if (window.innerWidth > 1536) {
					this.barWidth = 90;
				} else if (window.innerWidth > 1280) {
					this.barWidth = 70;
				} else {
					this.barWidth = 60;
				}
				
				this.barHeight = this.pageHeight;
				this.contentHeight = this.pageHeight;
				this.contentWidth = window.innerWidth - this.apartments.length * this.barWidth;
				this.barLeft = this.contentWidth;
				this.barTop = 0;
			}
		},
		selectApartment(index) {
			if (this.selectedApartment === index) {
				if (index === -1) return;

				index--;
			}

			gsap.killTweensOf(this.$refs[`apartment-0`][0].$el);

			this.$nextTick(() => {
				if (index > this.selectedApartment) {
					for (let i = this.selectedApartment + 1; i <= index; i++) {
						const vars = this.barLeft === 0 ? {
							x: 0,
							y: -this.contentHeight,
							duration: 0.5,
						} : {
							x: -this.contentWidth,
							y: 0,
							duration: 0.5,
						};

						gsap.to(this.$refs[`apartment-${i}`][0].$el, vars);
					}
				} else {
					for (let i = this.selectedApartment; i > index; i--) {
						const vars = this.barLeft === 0 ? {
							x: 0,
							y: 0,
							duration: 0.5,
						} : {
							x: 0,
							y: 0,
							duration: 0.5,
						};

						gsap.to(this.$refs[`apartment-${i}`][0].$el, vars);
					}
				}

				this.selectedApartment = index;
			});
		},
	},
	computed: {
		language() {
			return this.$i18n.locale;
		},
	},
	watch: {
		language() {
			this.$nextTick(() => {
				this.selectApartment(-1);
				this.calculateContentSize();
			});
		},
	},
};
</script>
